// src/app/components/PixBimViewer/PixBimViewer.tsx
import React, { useCallback, useMemo, useRef, useState, useEffect } from "react";
import type { Polygon } from "detect-collisions";
import { Box, System } from "detect-collisions";
import type { Graphics, PointData } from "pixi.js";

import type { PixBimMeasureType } from "./Components/PixBimMeasure";
import { Container, FederatedPointerEvent, Text } from "pixi.js";
import { Application, extend } from "@pixi/react";

import { getBoundingBox } from "./utils/geo";
import { invertScalePoint, scalePoint } from "./utils/pixbim-scale";
import { IPixCircle, IPixPath, IPixPolygon } from "./types";
import PixBimCircle from "./Components/PixBimCircle";
import PixBimPolygon from "./Components/PixBimPolygon";
import PixBimPath from "./Components/PixBimPath";
import PixBimTooltip from "./Components/PixBimTooltip";
import PixBimContextMenu from "./Components/PixBimContextMenu";
import PixBimRectangle from "./Components/PixBimRectangle";
import PixBimSelect from "./Components/PixBimSelect";
import PixBimMeasure from "./Components/PixBimMeasure";
import PixBimViewport from "./Components/PixBimViewport";

const system = new System();
const box = new Box({ x: 0, y: 0 }, 0, 0);
system.insert(box);

export type IWorldPoint = { x: number; y: number };

export type IPixBimCameraState = {
  zoom: number | undefined; // scale factor (world -> screen)
  centerWorld: IWorldPoint | undefined; // camera center in world coords
};

export type PixBimViewerAction = "normal" | "dragSelect" | "measure";

interface PixBimViewerProps {
  polygons: IPixPolygon[];
  paths: IPixPath[];
  circles?: IPixCircle[];
  angle: number;
  onMoveStart?: (id: string) => void;
  onClick?: (id: string | undefined, coordinates: [number, number]) => void;
  onDragAndDrop?: (id: string, points: number[][]) => void;
  onRightMenuClick?: (action: string, polygon: IPixPolygon) => void;
  onDragSelected?: (ids: string[]) => void;
  pointerTracker?: {
    color: string;
    size: number;
  };
  goToPolygon?: string; // id of polygon to go to
  action?: PixBimViewerAction;
  zoomExtentKey?: string | number | null; // ✅ NEW (controlled trigger)
  useLeftClickToPan?: boolean;

  // ---------------------------------------------------------------------------
  // ✅ Controlled camera (optional)
  // Hvis undefined => oppførsel som før
  // ---------------------------------------------------------------------------
  zoom?: number;
  centerWorld?: { x: number; y: number };
  onCameraChange?: (camera: IPixBimCameraState) => void;
}

interface DragObject extends Container {
  polygon?: IPixPolygon;
}

interface ViewerPolygon extends IPixPolygon {
  item?: Graphics;
}

interface CollisionPolygon extends Polygon {
  item?: Graphics;
  id?: string;
}

extend({ Container });
extend({ Text });

const DRAG_DELAY_MS = 100;
const SCALE = 100;

const debug = false;
let dragTimeoutId: ReturnType<typeof setTimeout> | false = false;

const PixBimViewer: React.FC<PixBimViewerProps> = (props) => {
  const {
    angle = 180,
    circles = [],
    polygons,
    onClick,
    onDragAndDrop,
    onDragSelected,
    onMoveStart,
    paths,
    onRightMenuClick,
    goToPolygon,
    action,
    zoomExtentKey,
    useLeftClickToPan = false,

    // controlled camera
    zoom: controlledZoom,
    centerWorld,
    onCameraChange,
  } = props;

  // ---------------------------------------------------------------------------
  // Pan modifier (Magic Mouse friendly)
  // ---------------------------------------------------------------------------
  // Revit-style: pan with middle mouse (handled in Viewport)
  // Fallback: hold Space + left mouse drag to pan (handled in Viewport)
  const [isPanKeyDown, setIsPanKeyDown] = useState(false);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.code === "Space") setIsPanKeyDown(true);
    };

    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code === "Space") setIsPanKeyDown(false);
    };

    const onBlur = () => setIsPanKeyDown(false);

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    window.addEventListener("blur", onBlur);

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("blur", onBlur);
    };
  }, []);

  // ✅ zoom extent button trigger
  const [fitTrigger, setFitTrigger] = useState(0);
  const lastZoomKey = useRef<string | number | undefined>(undefined);

  useEffect(() => {
    if (zoomExtentKey == null) return;
    if (lastZoomKey.current === zoomExtentKey) return;

    lastZoomKey.current = zoomExtentKey;
    setFitTrigger((v) => v + 1); // ✅ trigger zoom extent in Viewport
  }, [zoomExtentKey]);

  // --- Flip coordinates once per props change ---
  const flippedPolygons = useMemo(
    () =>
      polygons.map((polygon) => ({
        ...polygon,
        points: polygon.points.map(([x, y]) => [y, x]),
      })),
    [polygons]
  );

  const flippedCircles = useMemo(
    () =>
      circles.map((circle) => {
        const [x, y] = circle.position;
        return { ...circle, position: [y, x] as [number, number] };
      }),
    [circles]
  );

  const flippedPaths = useMemo(
    () =>
      paths.map((path) => ({
        ...path,
        points: path.points.map(([x, y]) => [y, x]),
      })),
    [paths]
  );

  const measureContainer = useRef<PixBimMeasureType | null>(null);
  const wrapContainer = useRef<any | false>(false);
  const dragTarget = useRef<DragObject | null>(null);
  const moved = useRef<boolean>(false);
  const pixBimSelectRef = useRef<any | null>(null);
  const [measurePoints, setMeasurePoints] = useState<PointData[]>([]);
  const [dragPoint, setDragPoint] = useState<PointData | null>(null);
  const [viewerContainer, setViewerContainer] = useState<HTMLDivElement | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<PointData>({ x: 0, y: 0 });
  const [contextMenu, setContextMenu] = useState<IPixPolygon | false>(false);
  const [contextMenuPosition, setContextMenuPosition] = useState<PointData>({ x: 0, y: 0 });
  const [tooltipTexts, setTooltipTexts] = useState<string[] | null>(null);

  const firstPolygon = flippedPolygons.length > 0 ? flippedPolygons[0] : { points: [[0, 0]] };
  const [firstPoint = [0, 0]] = firstPolygon.points;
  const [x, y] = firstPoint;

  const offSet = useMemo(() => ({ x, y }), [x, y]);

  useEffect(() => {
    if (action !== "measure" && measurePoints.length > 0) {
      setMeasurePoints([]);
    }
  }, [action, measurePoints.length]);

  // --- Scale all objects (expensive -> useMemo) ---
  const { scaledPolygons, scaledPaths, scaledCircles } = useMemo(() => {
    const ids: Map<string, number> = new Map();

    const scaledPaths = flippedPaths.map((path) => {
      const points = path.points.map((p) => scalePoint(p, offSet, SCALE));
      const { id } = path;

      const count = ids.get(id) || 0;
      ids.set(id, count + 1);

      return {
        ...path,
        id: path.id + count,
        points,
      };
    });

    const scaledPolygons = flippedPolygons.map((polygon) => {
      const points = polygon.points.map((p) => scalePoint(p, offSet, SCALE));
      return { ...polygon, points };
    });

    const scaledCircles = flippedCircles.map((circle) => {
      const position = scalePoint(circle.position, offSet, SCALE);
      return { ...circle, position };
    });

    return { scaledPolygons, scaledPaths, scaledCircles };
  }, [flippedPolygons, flippedPaths, flippedCircles, offSet]);

  // --- Bounding box, center and zoom (depends only on scaledPolygons) ---
  const boundingBox = useMemo(
    () => getBoundingBox(scaledPolygons.map((p) => p.points).flat(1)),
    [scaledPolygons]
  );

  const center: PointData = useMemo(
    () => ({
      x: boundingBox.x + boundingBox.width / 2,
      y: boundingBox.y + boundingBox.height / 2,
    }),
    [boundingBox]
  );

  const padding = 0;
  const zoomBox = useMemo(
    () => ({
      width: boundingBox.width + padding,
      height: boundingBox.height + padding,
      x: boundingBox.x - padding / 2,
      y: boundingBox.y - padding / 2,
    }),
    [boundingBox]
  );

  // Selection state (used during drag-select)
  let preSelected = new Map();
  let previousSelected = new Map();

  const onCircleClick = useCallback(
    (e: FederatedPointerEvent, circle: IPixCircle) => {
      if (onClick) {
        onClick(circle.id, [e.data.global.x, e.data.global.y]);
      }
    },
    [onClick]
  );

  const onPolygonClick = useCallback(
    (e: FederatedPointerEvent, polygon: IPixPolygon) => {
      if (isPanKeyDown) return;
      if (action === "measure") return;

      if (onClick && !moved.current) {
        onClick(polygon.id, [e.data.global.x, e.data.global.y]);
      }
      moved.current = false;
      e.stopPropagation();
    },
    [action, onClick, isPanKeyDown]
  );

  const onPolygonPointerDown = useCallback(
    (e: FederatedPointerEvent, polygon: IPixPolygon) => {
      if (isPanKeyDown) return;
      if (action === "dragSelect" || action === "measure") {
        return;
      }
      if (polygon.isMoveable) {
        e.stopPropagation();
        const target = e.currentTarget as DragObject;
        target.polygon = polygon;
        setDragPoint(e.getLocalPosition(target.parent));
        dragTimeoutId = setTimeout(() => {
          if (onMoveStart) {
            onMoveStart(polygon.id);
          }
          dragTarget.current = target;
        }, DRAG_DELAY_MS);
      }
    },
    [action, onMoveStart, isPanKeyDown]
  );

  const onPolygonHover = useCallback((e: FederatedPointerEvent, tooltipText?: string) => {
    if (e.global && tooltipText) {
      setTooltipTexts([tooltipText]);
      setTooltipPosition(e.global);
    }
  }, []);

  const onPolygonRightClick = useCallback((e: FederatedPointerEvent, polygon: IPixPolygon) => {
    e.stopPropagation();
    setContextMenu(false);
    if (polygon.rightClickMenu) {
      setContextMenu(polygon);
      setContextMenuPosition({ ...e.global });
    }
  }, []);

  const onPointerMoveOnStage = (e: FederatedPointerEvent) => {
    moved.current = true;

    if (action === "measure" && measurePoints.length === 1) {
      e.stopPropagation();
      if (measureContainer.current && wrapContainer.current) {
        const local = wrapContainer.current.toLocal(e.global);
        measureContainer.current.updateMeasure(local);
      }
    }

    if (action === "dragSelect") {
      e.stopPropagation();
      if (!wrapContainer.current) return;
      const local = wrapContainer.current.toLocal(e.global);
      const selectBox = pixBimSelectRef.current.setPos(local);
      if (!selectBox) return;

      const { x, y, width, height } = selectBox;

      box.width = width;
      box.height = height;
      box.setPosition(x, y, true);
      preSelected = new Map();

      system.checkOne(box, ({ b }) => {
        preSelected.set(b.id, b.item);
      });
      preSelected.forEach((item, key) => {
        if (previousSelected.has(key)) {
          previousSelected.delete(key);
        } else {
          item.setPreSelected();
        }
      });
      previousSelected.forEach((item) => {
        item.setNotPreSelected();
      });
      previousSelected = preSelected;
      return e.stopPropagation();
    }

    if (dragTarget.current && wrapContainer.current) {
      const newPosition = wrapContainer.current.toLocal(e.global);
      dragTarget.current.x = newPosition.x - (dragPoint?.x || 0);
      dragTarget.current.y = newPosition.y - (dragPoint?.y || 0);
    }
  };

  const onPointerDownOnStage = (e: FederatedPointerEvent) => {
    moved.current = false;

    if (!wrapContainer.current) return;

    if (action === "measure") {
      e.stopPropagation();
      const local = wrapContainer.current.toLocal(e.global);
      if (measurePoints.length === 1) {
        return setMeasurePoints([measurePoints[0], local]);
      }
      setMeasurePoints([local]);
    }

    if (action === "dragSelect") {
      const local = wrapContainer.current.toLocal(e.global);
      scaledPolygons.forEach(({ points, id, item }: ViewerPolygon) => {
        const p: CollisionPolygon = system.createPolygon(
          { x: 0, y: 0 },
          points.map((point) => ({ x: point[0], y: point[1] }))
        );
        p.id = id;
        p.item = item;
      });
      system.update();
      box.setPosition(local.x, local.y, true);

      pixBimSelectRef.current.setStart(local);
      pixBimSelectRef.current.setPos(local);
      return e.stopPropagation();
    }
  };

  const onPointerUpOnStage = (e: FederatedPointerEvent) => {
    if (action === "dragSelect") {
      onDragSelected && onDragSelected(Array.from(preSelected.keys()));
      pixBimSelectRef.current.setStart(false);
      pixBimSelectRef.current.setPos(false);
    }

    if (dragTimeoutId) {
      clearTimeout(dragTimeoutId);
    }

    if (dragTarget.current) {
      const { polygon } = dragTarget.current;
      const newPosition = e.getLocalPosition(dragTarget.current.parent);
      dragTarget.current.x = newPosition.x - (dragPoint?.x || 0);
      dragTarget.current.y = newPosition.y - (dragPoint?.y || 0);
      const moveOffset = {
        x: newPosition.x - (dragPoint?.x || 0),
        y: newPosition.y - (dragPoint?.y || 0),
      };
      dragTarget.current.x = 0;
      dragTarget.current.y = 0;
      dragTarget.current = null;

      if (!polygon) return;

      const scaledAndFlipped = polygon.points.map((p: number[]) => {
        const [x, y] = invertScalePoint(p, offSet, SCALE, moveOffset);
        return [y, x];
      });
      if (onDragAndDrop && polygon) {
        onDragAndDrop(polygon.id, scaledAndFlipped);
      }
    }
  };

  const onContextMenuClick = (action: string, polygon: IPixPolygon) => {
    onRightMenuClick && onRightMenuClick(action, polygon);
    setContextMenu(false);
  };

  const moveToBoundingBox = useMemo(() => {
    if (!goToPolygon) return undefined;
    const polygon = scaledPolygons.find((p) => p.id === goToPolygon);
    if (!polygon) return undefined;
    return { box: getBoundingBox(polygon.points) };
  }, [goToPolygon, scaledPolygons]);

  const onViewportMove = () => {
    setContextMenu(false);
  };

  const rects = useMemo(
    () => [
      { points: [boundingBox.x, boundingBox.y], fill: "#ff0000" },
      { points: [boundingBox.x + boundingBox.width, boundingBox.y], fill: "#0000ff" },
      { points: [boundingBox.x, boundingBox.y + boundingBox.height], fill: "#ff00ff" },
      {
        points: [boundingBox.x + boundingBox.width, boundingBox.y + boundingBox.height],
        fill: "#ff00ff",
      },
      { points: [center.x, center.y], fill: "#000000", width: 200, height: 200 },
    ],
    [boundingBox, center]
  );

  return (
    <div
      style={{ width: "100%", height: "100%", display: "flex", position: "relative" }}
      ref={(node: HTMLDivElement) => setViewerContainer(node)}
      onContextMenu={(e) => e.preventDefault()}
    >
      {viewerContainer && (
        <Application
          autoDensity
          autoStart
          resolution={window.devicePixelRatio}
          antialias={true}
          background={0xffffff}
          resizeTo={viewerContainer}
          width={viewerContainer?.clientWidth ?? window.innerWidth}
          height={viewerContainer?.clientHeight ?? window.innerHeight}
        >
          <container
            interactive
            eventMode="static"
            onPointerMove={onPointerMoveOnStage}
            onPointerUp={onPointerUpOnStage}
            onPointerDown={onPointerDownOnStage}
          >
            <PixBimViewport
              useLeftClickToPan={useLeftClickToPan}
              modelCenter={center}
              onMove={onViewportMove}
              boundingBox={zoomBox}
              action={action ?? "normal"}
              moveToBoundingBox={moveToBoundingBox?.box}
              fitTrigger={fitTrigger}
              panKeyDown={isPanKeyDown}
              // ✅ controlled camera (optional)
              zoom={controlledZoom}
              centerWorld={centerWorld}
              onCameraChange={onCameraChange}
            >
              <container pivot={center} position={center} angle={angle} ref={wrapContainer}>
                {scaledPolygons.map((polygon: ViewerPolygon) => (
                  <PixBimPolygon
                    ref={(ref: any) => {
                      polygon.item = ref;
                    }}
                    onClick={onPolygonClick}
                    {...(polygon.isMoveable && { onPointerDown: onPolygonPointerDown })}
                    {...(polygon.tooltip && { onPolygonHover })}
                    {...(polygon.rightClickMenu && { onRightClick: onPolygonRightClick })}
                    polygon={polygon}
                    angle={angle}
                    key={polygon.id}
                  />
                ))}
                {scaledPaths.map((path: IPixPath) => (
                  <PixBimPath path={path} key={path.id} />
                ))}
                {scaledCircles.map((circle: IPixCircle) => (
                  <PixBimCircle key={circle.id} circle={circle} onClick={onCircleClick} />
                ))}
                {debug &&
                  rects.map(({ points, fill }, i: number) => (
                    <PixBimRectangle
                      fill={fill}
                      key={`hej-${i}`}
                      point={points}
                      width={100}
                      height={100}
                    />
                  ))}
                <PixBimSelect ref={pixBimSelectRef} />
                {(measurePoints.length === 1 || measurePoints.length === 2) && (
                  <PixBimMeasure
                    points={measurePoints}
                    angle={angle}
                    scale={SCALE}
                    ref={measureContainer}
                  />
                )}
              </container>
            </PixBimViewport>

            <PixBimTooltip texts={tooltipTexts || []} position={tooltipPosition} />
            {contextMenu && (
              <PixBimContextMenu
                polygon={contextMenu}
                position={contextMenuPosition}
                onClick={onContextMenuClick}
              />
            )}
          </container>
        </Application>
      )}
    </div>
  );
};

export default PixBimViewer;
